import React, {memo, useMemo} from 'react';
import {useNavigation} from '@react-navigation/core';
import {View, Platform, Text} from 'react-native';
import {useQueryClient} from 'react-query';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import {useToast} from 'react-native-fast-toast';

import { StyleSheet } from 'react-native';
import {NavigationKey} from '../../navigations/config';
import {
  NotificationsResBody,
  NotificationType,
} from '../../server/types/notification.types';

import {
  RNEIconButton,
  RNEText,
  ShadowedCard,
  SVGIcon,
} from './../../components';
import {width, height, totalSize} from 'react-native-dimension';
import { colorPalette, colors } from '../../../core/config/color.config';
import moment from 'moment';
import useStores from '../../stores/useStores';
import {
  useMarkNotificationRead,
  useMarkNotificationReadByServiceRequest,
} from '../../server/react-query/useNotification';
import {syncUnreadNotificationIndicators, removeNotificationFromTray} from '../../utils/notification-badge.utils';
import {buildNotificationConsultationParams} from '../../utils/notification-navigation.utils';

interface NotificationItem {
  title: string;
  subtitle: string;
  stack: NavigationKey;
  screen: NavigationKey;
  consultationRelated: boolean;
  tab?: number;
  createdOn?: any;
  userName?: string,
  isRead?: boolean,
}

interface NotificationCardProps {
  notification: NotificationsResBody;
}

const NotificationCard = (props: NotificationCardProps) => {
  const {authStore, patientProfileStore} = useStores();
  const toast = useToast();
  
  const navigation = useNavigation();

  const {notification} = props;
  const queryClient = useQueryClient();

  const {mutateAsync: markReadByNotificationId} = useMarkNotificationRead();
  const {mutateAsync: markRead} = useMarkNotificationReadByServiceRequest();

  const parsedContent = useMemo(() => {
    if (!notification?.content) return null;
    if (typeof notification.content === 'string') {
      try {
        return JSON.parse(notification.content);
      } catch {
        return null;
      }
    }
    return notification.content;
  }, [notification?.content]);

  const redirect = async () => {
    const {stack, screen, consultationRelated, tab} = notificationItem;
    const serviceRequestId = parsedContent?.ServiceRequestId;
    const subject =
      authStore.userRole === 'patient'
        ? patientProfileStore.activeProfile?.patientId
        : undefined;

    if (consultationRelated && !serviceRequestId) {
      toast?.show('This looks like an older notification. Please select a conversation.', {
        type: 'warning',
      });

      if (stack) {
        (navigation as any).navigate(stack, {
          screen: NavigationKey.CONSULTATION_LIST,
          params: {
            tab: tab,
            screen: tab !== undefined ? getEquivalentScreen(tab) : undefined,
          },
        });
      }

      return;
    }

    if (stack && screen && consultationRelated) {
      (navigation as any).navigate(stack, {
        screen: screen,
        params: buildNotificationConsultationParams({
          tab: tab,
          screen: tab !== undefined ? getEquivalentScreen(tab) : undefined,
          id: serviceRequestId,
        }),
      });

      try {
        await markRead({
          serviceRequestId: serviceRequestId,
          subject,
        });
        removeNotificationFromTray(serviceRequestId);
        await syncUnreadNotificationIndicators(queryClient, subject);
      } catch (error) {
        console.log('Error reconciling unread notification state:', error);
      }
    } else if (stack && screen) {
      (navigation as any).navigate(stack, {
        screen: screen,
      });

      try {
        await markReadByNotificationId({
          notificationId: notification.notificationId,
          subject,
        });
        await syncUnreadNotificationIndicators(queryClient, subject);
      } catch (error) {
        console.log('Error reconciling unread notification state:', error);
      }
    }
  };

  const getEquivalentScreen = (tabNum: number) => {
    let screenName = '';
    switch (tabNum) {
      case 0:
        screenName = NavigationKey.CONSULTATION_TAB_PENDING;
        break;

      case 1:
        screenName = NavigationKey.CONSULTATION_TAB_ONGOING;
        break;

      case 2:
        screenName = NavigationKey.CONSULTATION_TAB_HISTORY;
        break;
    }
    return screenName;
  };

  const notificationItem: NotificationItem = useMemo(() => {
    let userName = '';
    if(authStore.userRole === 'patient') {
      if(notification?.practitioner){
        if(notification?.practitioner?.middleName){
          userName = notification?.practitioner?.firstName+' '+notification?.practitioner?.middleName+' '+notification?.practitioner?.lastName;
        }else{
          userName = notification?.practitioner?.firstName+' '+notification?.practitioner?.lastName;
        }
      }
    }else{
      if(notification?.patient){
        if(notification?.patient?.middleName){
          userName = notification?.patient?.firstName+' '+notification?.patient?.middleName+' '+notification?.patient?.lastName;
        }else{
          userName = notification?.patient?.firstName+' '+notification?.patient?.lastName;
        }
      }
    }

    switch (notification.type) {
      case NotificationType.CONSULTATION_REQUEST:
        return {
          title: 'Consultation Request',
          subtitle: 'You have received a consultation request.',
          stack: NavigationKey.DOCTOR_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 1,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.CONSULTATION_CANCELLED:
        return {
          title: 'Consultation Cancelled',
          subtitle: 'A consultation has been cancelled by a patient.',
          stack: NavigationKey.DOCTOR_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 2,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.CONSULTATION_ACCEPTED:
        return {
          title: 'Consultation Request Accepted',
          subtitle:
            'Your consultation request has been accepted by the doctor. You can now pay for the consultation.',
          stack: NavigationKey.PATIENT_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 0,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.CONSULTATION_DECLINED:
        return {
          title: 'Consultation Declined',
          subtitle:
            'Your consultation request has been declined by the doctor.',
          stack: NavigationKey.PATIENT_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 2,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.CONSULTATION_PAID:
        return {
          title: 'Consultation Paid',
          subtitle:
            'A consultation has been paid by a patient. You can start the consultation.',
          stack: NavigationKey.DOCTOR_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 0,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.CONSULTATION_COMPLETED:
        return {
          title: 'Consultation Completed',
          subtitle:
            'Your consultation has been set as completed by the doctor.',
          stack: NavigationKey.PATIENT_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 2,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.PRACTITIONER_PROFILE_PENDING:
        return {
          title: 'Profile Pending Review',
          subtitle:
            'You have sent a request to verify your profile. Verification process normally takes 24 to 48 hours.',
          stack: NavigationKey.DOCTOR_PROFILE_STACK,
          screen: NavigationKey.DOCTOR_VIEW_PROFESSIONAL_PROFILE,
          consultationRelated: false,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.PRACTITIONER_PROFILE_DECLINED:
        return {
          title: 'Profile Declined',
          subtitle:
            'Your verification request has been declined. Please ensure you have provided the complete and correct details.',
          stack: NavigationKey.DOCTOR_PROFILE_STACK,
          screen: NavigationKey.DOCTOR_VIEW_PROFESSIONAL_PROFILE,
          consultationRelated: false,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.PRACTITIONER_PROFILE_VERIFIED:
        return {
          title: 'Profile Verified',
          subtitle:
            'Your verification request has been accepted. Patients will now be able to see your profile.',
          stack: NavigationKey.DOCTOR_PROFILE_STACK,
          screen: NavigationKey.DOCTOR_VIEW_PROFESSIONAL_PROFILE,
          consultationRelated: false,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.ATTACHED_DIAGNOSTIC_REPORT:
        return {
          title: 'Diagnostic Report Attached',
          subtitle:
            'The doctor attached a diagnostic report in your consultation chat.',
          stack: NavigationKey.PATIENT_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 0,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.ATTACHED_MEDICATION_REQUEST:
        return {
          title: 'Medication Request Attached',
          subtitle:
            'The doctor attached a medication request in your consultation chat.',
          stack: NavigationKey.PATIENT_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 0,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.ATTACHED_LABORATORY_REQUEST:
        return {
          title: 'Laboratory Certificate Attached',
          subtitle:
            'The doctor attached a laboratory request in your consultation chat.',
          stack: NavigationKey.PATIENT_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 0,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };

      case NotificationType.MESSAGE_ADDED:
        if (parsedContent?.IsTest) {
          return {
            title: 'Test Message',
            subtitle: 'This is a test notification for routing.',
            stack: authStore.userRole === 'patient'
              ? NavigationKey.PATIENT_CONSULTATION_STACK
              : NavigationKey.DOCTOR_CONSULTATION_STACK,
            screen: NavigationKey.CONSULTATION_DETAIL,
            consultationRelated: true,
            tab: 1,
            createdOn: notification.createdOn,
            userName: userName,
            isRead: notification.isRead,
          };
        }
        return {
          title: 'New Message Received',
          subtitle: 'Your consultation chat has a new message.',
          stack: authStore.userRole === 'patient'
            ? NavigationKey.PATIENT_CONSULTATION_STACK
            : NavigationKey.DOCTOR_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 1,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };
      case NotificationType.APPOINTMENT_REQUEST:
        return {
          title: 'New Appointment Scheduled',
          subtitle: 'An appointment has been scheduled for you.',
          stack: authStore.userRole === 'patient'
            ? NavigationKey.PATIENT_CONSULTATION_STACK
            : NavigationKey.DOCTOR_CONSULTATION_STACK,
          screen: NavigationKey.CONSULTATION_DETAIL,
          consultationRelated: true,
          tab: 0,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };
      default:
        return {
          title: 'Notification',
          subtitle: '',
          stack: authStore.userRole === 'patient'
            ? NavigationKey.PATIENT_NOTIFICATION_STACK
            : NavigationKey.DOCTOR_NOTIFICATION_STACK,
          screen: NavigationKey.PATIENT_NOTIFICATIONS,
          consultationRelated: false,
          createdOn: notification.createdOn,
          userName: userName,
          isRead: notification.isRead,
        };
    }
  }, [notification, parsedContent]);

  return (
    <>
      {Platform.OS === 'web' ? (
        <ShadowedCard height={120} onPress={redirect}>
          <View style={styles.textContainer}>
            <RNEText
              label={notificationItem?.title || ''}
              variant="title"
              weight="bold"
              color="primary"
              style={titleFontSizeWeb}
            />
            <View style={styles.subtitleContainer}>
              <RNEText
                label={notificationItem?.subtitle || ''}
                variant="body"
              />
            </View>
          </View>
          <RNEIconButton
            icon={<SVGIcon name="check-white" size={32} />}
            color="secondary"
            size={48}
          />
        </ShadowedCard>
      ) : (
        <ShadowedCard  onPress={redirect} style={{marginBottom: height(0.1), width: width(100), paddingBottom:0, paddingTop:0,paddingRight:totalSize(1), flexDirection:'column', alignItems: 'flex-start', shadowOpacity:0,shadowColor: 'white', borderBottomWidth:totalSize(0.1), borderColor: colors.gray.lighter}}>
          <View style={[styles.textContainer, {flexDirection:'row', justifyContent:'space-between', width: width(85)}]}>
            <RNEText
              label={notificationItem?.title || ''}
              variant="title"
              weight="bold"
              style={titleFontSizeMobile}
            />

            {
              !notificationItem?.isRead && (
                <View style={{backgroundColor:colorPalette.secondary, borderRadius: totalSize(5), width: width(4), height: width(4),marginTop: totalSize(1)}}>
                  <Text></Text>
                </View>
              )
            }
          </View>

           <View style={styles.textContainer}>
            <RNEText
              label={notificationItem?.subtitle || ''}
              variant="title"
              weight="400"
              style={subTitleFontSizeMobile}
            />
          </View>

          <View style={{width:'100%',flexDirection:'row',flex:1, alignContent:'space-around', alignItems:'flex-start', alignSelf:'flex-start',justifyContent:'space-around', marginBottom: totalSize(1),marginTop: totalSize(1)}}>
          <View style={{flex:1}}>
            <Text style={{fontFamily:'Quicksand-Regular', fontSize: totalSize(1.6), color: colors.black.regular}}>
              {notificationItem?.userName || ''}
            </Text>
          </View>

          <View>
            <Text style={{fontFamily:'Quicksand-Regular', fontSize: totalSize(1.6), color: colorPalette.black}}>
              {moment.utc(notificationItem?.createdOn).local().format('MMMM DD, YYYY')}
            </Text>
          </View>
          </View>

          {/* <MaterialIcons
            name="arrow-circle-right"
            color={colorPalette.secondary}
            size={totalSize(4.5)}
          /> */}
          {/* <RNEIconButton
            icon={}
            // color="red"
            size={48}
            raised={true}
          /> */}
        </ShadowedCard>
      )}
    </>
  );
};

export default memo(NotificationCard);

const titleFontSizeWeb = {
  fontSize: 20,
};

const titleFontSizeMobile = {
  fontFamily: 'Quicksand-Bold',
  fontSize: totalSize(2),
};

const subTitleFontSizeMobile = {
  fontFamily: 'Quicksand-Medium',
  fontSize: totalSize(2),
};

const styles = StyleSheet.create({
  textContainer: {
    marginTop: totalSize(0.1),
    // width: Platform.OS === 'web' ? '95%' : '90%',
  },
  subtitleContainer: {
    marginTop: 4,
  },
});
